import { Tooltip } from "@alch/ui";
import clsx from "clsx";
import { ToggleGroup } from "radix-ui";
import type { ReactNode } from "react";

export interface SegmentedToggleItem {
  label: string;
  value: string;
  disabled?: boolean;
  tooltipContent?: ReactNode;
}

interface SegmentedToggleProps {
  items: SegmentedToggleItem[];
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
}

const itemClassName = clsx(
  "flex h-6 items-center justify-center rounded px-2.5 text-label-100 transition",
  "text-tertiary enabled:hover:text-secondary",
  "data-[state=on]:bg-card data-[state=on]:text-secondary data-[state=on]:shadow-sm",
  "outline-hidden focus-visible:shadow-focus",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

const SegmentedToggle = ({
  items,
  value,
  onValueChange,
  ariaLabel,
  className,
}: SegmentedToggleProps) => {
  return (
    <ToggleGroup.Root
      type="single"
      rovingFocus={false}
      aria-label={ariaLabel}
      value={value}
      onValueChange={(newValue) => {
        if (newValue) {
          onValueChange(newValue);
        }
      }}
      className={clsx(
        "flex h-8 items-center gap-1 rounded-lg border border-primary bg-subtle p-1",
        className,
      )}
    >
      {items.map((item) => {
        if (item.disabled && item.tooltipContent) {
          return (
            <Tooltip
              key={item.value}
              content={item.tooltipContent}
              withArrow
              side="top"
            >
              <span className="inline-flex">
                <ToggleGroup.Item
                  value={item.value}
                  disabled
                  className={itemClassName}
                >
                  {item.label}
                </ToggleGroup.Item>
              </span>
            </Tooltip>
          );
        }

        return (
          <ToggleGroup.Item
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={itemClassName}
          >
            {item.label}
          </ToggleGroup.Item>
        );
      })}
    </ToggleGroup.Root>
  );
};

export default SegmentedToggle;
